import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Auth0Provider } from '@auth0/auth0-react';
import { CalWebSocketProvider } from './contexts/CalWebSocketContext';
import { CapabilitiesProvider } from './contexts/CapabilitiesContext';

// ---------------------------------------------------------------------------------------------------
// Identity configuration MUST come from the build environment. There is deliberately NO fallback.
//
// These previously defaulted to the Ohio demo's tenant and client id. Create React App inlines
// process.env.REACT_APP_* at build time, so a silo whose build did not receive these variables would
// silently ship a frontend that authenticated against the DEMO tenant in the United States -- with no
// error, and nothing on screen to show it. That is the same class of bug as G19, where one file read a
// variable nobody set and fell through to the demo silo's API.
//
// A missing value now stops the app immediately. Failing loudly in a build we control is strictly better
// than a UK silo quietly pointing its login at a US tenant.
// ---------------------------------------------------------------------------------------------------
function requiredEnv(name: string): string {
  const value = process.env[name];
  if (!value) {
    throw new Error(
      `${name} is not set. The silo's identity configuration did not reach this build, ` +
      `so authentication cannot be configured safely. Refusing to start.`
    );
  }
  return value;
}

const AUTH0_DOMAIN = requiredEnv('REACT_APP_AUTH0_DOMAIN');
const AUTH0_CLIENT_ID = requiredEnv('REACT_APP_AUTH0_CLIENT_ID');

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    {/* Silo-aware identity: one build serves every silo, so the Auth0 tenant/client/audience come from the
        build environment (Amplify env vars set from the silo manifest). See requiredEnv above — there is no
        fallback, deliberately. A silo with its own Auth0 Organization also passes organization, which makes
        Auth0 reject users who are not members — the tenant-crossing guard. */}
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        ...(process.env.REACT_APP_AUTH0_AUDIENCE ? { audience: process.env.REACT_APP_AUTH0_AUDIENCE } : {}),
        ...(process.env.REACT_APP_AUTH0_ORGANIZATION ? { organization: process.env.REACT_APP_AUTH0_ORGANIZATION } : {}),
      }}
    >
      <CapabilitiesProvider>
        <CalWebSocketProvider>
          <App />
        </CalWebSocketProvider>
      </CapabilitiesProvider>
    </Auth0Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
